'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'

import { useCartStore } from 'modules/cart'
import {
  CustomerInvoiceAddress,
  findDefaultInvoiceAddress,
  isAddressExistInAddresses,
  useCustomerAddresses
} from 'modules/customer'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { LocalStorage } from 'shared/utils/storage'

const invoiceStorage = new LocalStorage('invoice_data')

const defaultInvoiceData: InvoiceData = {
  wantsInvoice: false,
  selectedAddressId: ''
}

const getDefaultInvoiceDataId = (addresses: CustomerInvoiceAddress[]) => {
  const storedAddressId = invoiceStorage.get<InvoiceData | null>(
    null,
    validationLocalStorageObject
  )?.selectedAddressId

  const isExistAddressInStorage =
    storedAddressId && isAddressExistInAddresses(addresses, storedAddressId)

  if (isExistAddressInStorage) {
    return storedAddressId
  }

  const defaultAddressId = findDefaultInvoiceAddress(addresses)

  if (defaultAddressId) {
    return defaultAddressId
  }

  return ''
}

type SetInvoiceDataProps = InvoiceData | ((p: InvoiceData) => InvoiceData)
interface InvoiceData {
  wantsInvoice: boolean
  selectedAddressId: string
}

const validationLocalStorageObject = {
  wantsInvoice: 'boolean',
  selectedAddressId: 'string'
}

export const useCartInvoiceSelection = () => {
  const { invoiceAddresses, isLoadingInvoice, isPendingInvoice } =
    useCustomerAddresses()
  const addresses = useMemo(() => invoiceAddresses || [], [invoiceAddresses])
  const { addAddressToCart, detachDeliveryOrInvoiceAddressFromCart, cart } =
    useCartStore()
  const session = useSession()

  const [invoiceData, setInvoiceData] =
    useState<InvoiceData>(defaultInvoiceData)

  const setLocalInvoiceData = useCallback((data: InvoiceData) => {
    invoiceStorage.set(data)
    setInvoiceData(data)
  }, [])

  //sync invoice data with cart
  useEffect(() => {
    if (!cart) return

    if (!cart.invoiceData?.id) {
      setLocalInvoiceData(defaultInvoiceData)

      return
    }

    if (cart.invoiceData.id === invoiceData.selectedAddressId) return

    setLocalInvoiceData({
      wantsInvoice: true,
      selectedAddressId: cart.invoiceData.id || ''
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.invoiceData?.id])

  const updateInvoiceData = useCallback(
    async (newState: InvoiceData) => {
      if (!newState.wantsInvoice) {
        const previousInvoiceData = { ...invoiceData }

        try {
          setLocalInvoiceData({
            wantsInvoice: false,
            selectedAddressId: ''
          })

          return await detachDeliveryOrInvoiceAddressFromCart(
            'invoice',
            session
          )
        } catch (err) {
          setLocalInvoiceData(previousInvoiceData)
          throw err
        }
      }

      if (newState.selectedAddressId) {
        const previousInvoiceData = { ...invoiceData }

        try {
          setLocalInvoiceData(newState)
          await addAddressToCart(String(newState.selectedAddressId), session)
        } catch (err) {
          setLocalInvoiceData(previousInvoiceData)
          throw err
        }
      }
    },
    [
      setLocalInvoiceData,
      detachDeliveryOrInvoiceAddressFromCart,
      session,
      addAddressToCart,
      invoiceData
    ]
  )

  const handleSetInvoiceData = useCallback(
    async (data: SetInvoiceDataProps) => {
      try {
        const newState = typeof data === 'function' ? data(invoiceData) : data
        const isChangedInvoice =
          newState.selectedAddressId !== invoiceData.selectedAddressId ||
          newState.wantsInvoice !== invoiceData.wantsInvoice

        if (!isChangedInvoice) return

        const isNeedToFindInvoice =
          !newState.selectedAddressId && newState.wantsInvoice

        if (!isNeedToFindInvoice) {
          return await updateInvoiceData(newState)
        }

        const defaultAddress = getDefaultInvoiceDataId(addresses)

        if (defaultAddress) {
          return await updateInvoiceData({
            wantsInvoice: newState.wantsInvoice,
            selectedAddressId: defaultAddress
          })
        }

        return setLocalInvoiceData(newState)
      } catch (err) {
        toast.error(getApiErrors(err))
        throw err
      }
    },
    [invoiceData, updateInvoiceData, addresses]
  )

  const handleDetachInvoiceAddressFromCart = useCallback(async () => {
    const previousInvoiceData = { ...invoiceData }

    try {
      setLocalInvoiceData({
        wantsInvoice: false,
        selectedAddressId: ''
      })
      await detachDeliveryOrInvoiceAddressFromCart('invoice', session)
    } catch (err) {
      setLocalInvoiceData(previousInvoiceData)

      throw err
    }
  }, [
    detachDeliveryOrInvoiceAddressFromCart,
    invoiceData,
    session,
    setLocalInvoiceData
  ])

  const initStoredInvoiceData = useCallback(async () => {
    const defaultAddress = getDefaultInvoiceDataId(addresses)

    if (defaultAddress) {
      return await updateInvoiceData({
        wantsInvoice: true,
        selectedAddressId: defaultAddress
      })
    }
  }, [addresses, updateInvoiceData])

  return {
    invoiceData,
    isPendingInvoice,
    setInvoiceData: handleSetInvoiceData,
    detachDeliveryOrInvoiceAddressFromCart: handleDetachInvoiceAddressFromCart,
    initStoredInvoiceData,
    addresses,
    isLoadingInvoice
  } as const
}
